import Button from "components/atoms/Button/Button";
import Icon from "components/atoms/Icon/Icon";
import LeadScore from "components/atoms/LeadScore/LeadScore";
import Tooltip from "components/atoms/Tooltip/Tooltip";
import Container from "components/atoms/layout/Container/Container";
import LeadViewSkeleton from "components/atoms/layout/LeadViewSkeleton/LeadViewSkeleton";
import Skeleton from "components/atoms/layout/Skeleton/Skeleton";
import LeadHelper from "helpers/LeadHelper";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getContactRevealedByID, getLeadItem } from "store/slices/data.slice";
import { getClientICP } from "store/slices/icp.slice";
import {
  getLeadScore,
  getLeadStats,
  handleRevealContactByID,
} from "store/slices/insights";
import { getLeadVisits } from "store/slices/leadVisited";
import { getBalance } from "store/slices/user.slice";

import constants from "utils/constants";
import { LeadProfileDTO } from "utils/dtos/leadDTO";
import { messages } from "utils/message";
import { limitSeconds } from "utils/workWithData";
import { CompanyInfo } from "../Template/companyInfo";
import { Conversions } from "../Template/conversions";
import { PersonalInfo } from "../Template/personalInfo";
import { PageVisited } from "../Template/pageVisited";
import { setCurrentPage } from "store/slices/ui.slice";
import { Card } from "components/atoms/Card";
import { Tabs } from "antd";

const badgesClass = "flex items-center gap-2 text-sm text-gray-300";
const LeadViewModule: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const { leadId } = useParams();

  const [lead, setLead] = useState<LeadProfileDTO | undefined>();
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const { data: user } = useAppSelector((state) => state.user);
  const { data: ICPData, isLoading: ICPIsLoading } = useAppSelector(
    (state) => state.icp
  );
  const previousRoute = useAppSelector((state) => state.ui.previousRoute);

  const appSource = previousRoute?.includes("/database")
    ? constants.LEADSEARCH_KEYWORD
    : constants.PIXEL_PAGE;
  const {
    isLoading: CRPixelIsLoading,
    isSuccess: CRPixelIsSuccess,
    data: CRPixelData,
  } = useAppSelector((state) =>
    state.insights.contactsRevealed.find((x) => x.id === leadId)
  ) ?? {};

  const {
    isLoading: CRSearchIsLoading,
    isSuccess: CRSearchIsSuccess,
    data: CRSearchData,
  } = useAppSelector((state) =>
    state.data.contactsRevealed.find((x) => x.id === leadId)
  ) ?? {};

  const [isLoading, setIsLoading] = useState(true);

  const hasValidStats = useMemo(() => {
    if (!lead) return false;
    return (
      lead.leadScoreIcp !== 0 ||
      lead.leadScoreBehavior !== 0 ||
      lead.visits !== 0 ||
      lead.pageViews !== 0 ||
      lead.duration !== 0
    );
  }, [lead]);

  const fetchLead = async () => {
    if (!leadId || !isLoading || !user.clientId) {
      setIsLoading(false);
      return;
    }

    const res = await dispatch(getLeadItem({ leadId, source: appSource }));

    let scoreData = {
      icp: 0,
      behavior: 0,
    };

    try {
      const result = await dispatch(
        getLeadScore({ leadId: leadId, clientId: user.clientId })
      ).unwrap();
      scoreData = {
        icp: result.data.icp,
        behavior: result.data.behavior,
      };
    } catch (error) {
      console.error("Failed to fetch lead score", error);
    }

    if (res.payload?.data) {
      setLead({
        ...res.payload.data,
        leadScoreIcp: scoreData.icp,
        leadScoreBehavior: scoreData.behavior,
      });
      fetchLeadPulseStats();
    }

    setIsLoading(false);
  };

  const fetchLeadPulseStats = async () => {
    setIsStatsLoading(true);
    const res = await dispatch(getLeadStats({ leadId: leadId! }));
    setIsStatsLoading(false);
    if (!res.payload?.data) return;
    const leadStats = res.payload.data;
    setLead((prev: any) => ({
      ...prev,
      visitLast: leadStats.visitLast,
      visits: leadStats.visits,
      visitors: leadStats.visitors,
      pageViews: leadStats.pages,
      duration: leadStats.duration,
    }));
  };

  const fullName = useMemo(() => {
    if (!lead) return "";

    const parts = [lead?.nameFirst, lead?.nameLast].filter(Boolean);
    const concatNameAndLastname = parts.join(" ");

    if (concatNameAndLastname) {
      window.document.title = `${concatNameAndLastname} - ${constants.BRAND_NAME}`;
      return concatNameAndLastname;
    }

    return "";
  }, [lead]);

  const tabItems = lead && [
    {
      label: "Person Info",
      key: "1",
      children: <PersonalInfo lead={lead} />,
    },
    {
      label: "Company Info",
      key: "2",
      children: <CompanyInfo lead={lead} />,
    },
    {
      label: "Behavior",
      key: "3",
      children: <PageVisited lead={lead} />,
    },
  ];

  if (lead?.formData && tabItems) {
    tabItems.push({
      label: "Conversions",
      key: "4",
      children: <Conversions lead={lead} />,
    });
  }

  useEffect(() => {
    window.analytics.page("Lead Details", {
      leadId,
      source: appSource,
    });
    fetchLead();
  }, [user]);

  useEffect(() => {
    if (!CRPixelIsSuccess) return;
    if (CRPixelData) {
      setLead((prev) => ({ ...prev, ...CRPixelData, hideContactInfo: false }));
      setTimeout(() => {
        dispatch(getBalance());
      }, 10000);
      if (LeadHelper.hasContactData(CRPixelData))
        toast.success(messages.ENRICHMENT_CONTACTREVEAL_SUCCESS, {
          toastId: "contactRevealed",
        });
      else toast.error(messages.ENRICHMENT_CONTACTREVEAL_CONTACT_MISSING);
    } else {
      toast.error(messages.ENRICHMENT_CONTACTREVEAL_FAILED);
    }
  }, [CRPixelIsSuccess, user]);

  useEffect(() => {
    if (!CRSearchIsSuccess) return;
    if (CRSearchData) {
      setLead((prev) => ({
        ...prev,
        ...CRSearchData,
        visitLast: prev?.visitLast,
        visits: prev?.visits,
        visitors: prev?.visitors,
        pageViews: prev?.pageViews,
        duration: prev?.duration,
        hideContactInfo: false,
      }));
      setTimeout(() => {
        dispatch(getBalance());
      }, 10000);
      if (LeadHelper.hasContactData(CRSearchData))
        toast.success(messages.ENRICHMENT_CONTACTREVEAL_SUCCESS, {
          toastId: "contactRevealed",
        });
      else toast.error(messages.ENRICHMENT_CONTACTREVEAL_CONTACT_MISSING);
    } else {
      toast.error(messages.ENRICHMENT_CONTACTREVEAL_FAILED);
    }
  }, [CRSearchIsSuccess]);

  useEffect(() => {
    dispatch(getClientICP());
  }, [dispatch]);

  useEffect(() => {
    if (leadId) dispatch(getLeadVisits(leadId));
  }, [dispatch, leadId]);

  useEffect(() => {
    dispatch(setCurrentPage("back"));
  }, []);

  if (isLoading) return <LeadViewSkeleton />;
  if (!lead) {
    return (
      <Container>
        <div className="flex flex-col items-center justify-center text-white-500 text-lg gap-3 sm:pt-48">
          <Icon name="sad" size={60} />
          Lead not found
        </div>
      </Container>
    );
  }
  return (
    <Container className="gap-4 flex-1 h-full p-10 overflow-y-scroll">
      <Card hiddenHeader>
        <div className="flex flex-col md:flex-row sm:gap-9 max-sm:gap-3">
          <div className="flex items-center gap-5">
            <h1 className="font-bold text-sm">{fullName}</h1>
          </div>
          {!isLoading && hasValidStats && (
            <div className="flex items-center gap-2">
              <Tooltip content="ICP">
                <div className="flex flex-row gap-2 items-center justify-center text-white">
                  <LeadScore
                    iconName="target"
                    value={lead.leadScoreIcp || 0}
                    threshold={ICPData?.thresholdMinimumIcp || 0}
                    loading={isLoading && ICPIsLoading}
                  />
                </div>
              </Tooltip>
              <Tooltip content="Behavior">
                <div className="flex flex-row gap-2 items-center justify-center text-white">
                  <LeadScore
                    iconName="behavior"
                    value={lead.leadScoreBehavior || 0}
                    threshold={ICPData?.thresholdMinimumBehavior || 0}
                    loading={isLoading && ICPIsLoading}
                  />
                </div>
              </Tooltip>
              <Tooltip content="Visits">
                <div className={badgesClass}>
                  <div className="rounded-md flex bg-gray-100 w-fit text-sm justify-between px-3 max-h-[24px] gap-2 items-center font-semibold text-white-500 py-4">
                    <Icon size={13} name="mouse" />
                    {isStatsLoading ? (
                      <Skeleton width={20} height={20} />
                    ) : (
                      lead.visits || 0
                    )}
                  </div>
                </div>
              </Tooltip>
              <Tooltip content="Page Views">
                <div className={badgesClass}>
                  <div className="rounded-md flex bg-gray-100 w-fit text-sm justify-between px-3 max-h-[24px] gap-2 items-center font-semibold text-white-500 py-4">
                    <Icon size={13} name="page" />
                    {isStatsLoading ? (
                      <Skeleton width={20} height={20} />
                    ) : (
                      lead.pageViews || 0
                    )}
                  </div>
                </div>
              </Tooltip>
              <Tooltip content="Duration">
                <div className={badgesClass}>
                  <div className="rounded-md flex bg-gray-100 w-fit text-sm justify-between px-3 max-h-[24px] gap-2 items-center font-semibold text-white-500 py-4">
                    <Icon size={13} name="clock" />
                    {isStatsLoading ? (
                      <Skeleton width={20} height={20} />
                    ) : (
                      limitSeconds(lead.duration || 0)
                    )}
                  </div>
                </div>
              </Tooltip>
            </div>
          )}

          {lead.hideContactInfo && (
            <>
              {CRSearchIsLoading || CRPixelIsLoading ? (
                <div>
                  <LeadViewSkeleton />
                </div>
              ) : (
                <div className="ml-auto">
                  <Button
                    size="sm"
                    iconName="credits"
                    iconSize={18}
                    className="!h-[35px]"
                    onClick={(e: any) => {
                      if (appSource === constants.PIXEL_KEYWORD)
                        dispatch(handleRevealContactByID({ leadId: leadId! }))
                          .unwrap()
                          .then()
                          .catch((error) => {
                            toast.error(error?.message);
                          })
                          .finally(() => {
                            dispatch(getBalance());
                          });
                      else {
                        dispatch(getContactRevealedByID({ leadId: leadId! }))
                          .unwrap()
                          .then()
                          .catch((error) => {
                            toast.error(error?.message);
                          });
                      }
                    }}
                  >
                    Reveal Contact
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
      {/* <div className="grid grid-cols-12 flex-1 gap-16 h-full mt-6">
        <div className="col-span-6 flex flex-col gap-16">
          <PersonalInfo lead={lead} />
          {lead.formData && <Conversions lead={lead} />}
        </div>
        <div className="col-span-6 flex flex-col gap-16">
          <CompanyInfo lead={lead} />
          <PageVisited lead={lead} />
        </div>
      </div> */}
      <div>
        {/* TODO mudar para um arquivo separado depois */}
        <Tabs
          animated={true}
          type="card"
          items={tabItems}
          className="
          [&_.ant-tabs-tab]:!border-[var(--color-gray-700)]
          [&_.ant-tabs-tab]:!border-[1px]
          [&_.ant-tabs-tab]:!rounded-t-md

          [&_.ant-tabs-tab-btn]:!text-[var(--color-white-500)] 
          [&_.ant-tabs-tab-btn]:!font-semibold

          [&_.ant-tabs-tab:hover_.ant-tabs-tab-btn]:!text-[var(--color-primary-500)]
          [&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-[var(--color-white-500)]

          [&_.ant-tabs-tab-active]:!bg-[var(--color-primary-700)]
          [&_.ant-tabs-tab-active]:!font-semibold
          [&_.ant-tabs-nav]:!border-b-[var(--color-gray-700)]
          [&_.ant-tabs-nav::before]:!border-b-[var(--color-gray-700)]"
        />
      </div>
    </Container>
  );
};
export default LeadViewModule;
